import React, { useState } from "react";
import { useStore } from "../state/store";
import { readAddon, type AddonContents } from "../import/mcaddon";
import { analyzeAddon, type AddonAnalysis } from "../import/analyze";
import { projectFromAddon } from "../import/toProject";
import { ConflictList } from "../ui/common";
import { readFileAsBytes } from "../ui/files";

export function ImportPanel() {
  const project = useStore((s) => s.project);
  const replaceProject = useStore((s) => s.replaceProject);

  const [contents, setContents] = useState<AddonContents | null>(null);
  const [analysis, setAnalysis] = useState<AddonAnalysis | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      const bytes = await readFileAsBytes(file);
      const parsed = readAddon(bytes);
      setContents(parsed);
      setAnalysis(analyzeAddon(parsed, project));
    } catch (e) {
      setError(`Could not read "${file.name}": ${(e as Error).message}`);
      setContents(null);
      setAnalysis(null);
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const importAsProject = () => {
    if (!contents || !analysis) return;
    if (!confirm("Replace your current project with the imported add-on? Save your work first if needed.")) return;
    replaceProject(projectFromAddon(contents, analysis));
  };

  return (
    <div className="panel">
      <h2>Import an add-on</h2>
      <p className="hint">
        Drop a <code>.mcaddon</code>, <code>.mcpack</code>, or pack <code>.zip</code>. We read its lang files, list every
        identifier and namespace, and flag conflicting items, without changing anything until you choose to import.
      </p>

      <div
        className={`dropzone ${dragOver ? "over" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
      >
        <p>Drag &amp; drop a file here</p>
        <label className="btn primary">
          Choose file…
          <input
            type="file"
            accept=".mcaddon,.mcpack,.zip,application/zip"
            hidden
            onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
          />
        </label>
      </div>

      {busy && <p className="muted">Reading…</p>}
      {error && <p className="error-text">{error}</p>}

      {analysis && contents && (
        <>
          <section className="card">
            <div className="list-header">
              <h3>Analysis</h3>
              <button className="btn primary" onClick={importAsProject}>
                Import as project
              </button>
            </div>
            <div className="stat-grid">
              <Stat label="Packs" value={analysis.packs.length} />
              <Stat label="Items" value={analysis.items.length} />
              <Stat label="Recipes" value={analysis.recipes.length} />
              <Stat label="Categories" value={analysis.guide.categories.length} />
              <Stat label="Guide entries" value={analysis.guide.totalEntries} />
              <Stat label="Lang keys" value={analysis.langKeys.length} />
            </div>
            {analysis.primaryNamespace && (
              <p className="preview-line">
                Detected primary namespace: <code>{analysis.primaryNamespace}</code>
              </p>
            )}
          </section>

          {analysis.namespaces.length > 0 && (
            <section className="card">
              <h3>Namespaces used</h3>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Namespace</th>
                    <th>Items</th>
                    <th>Recipes</th>
                  </tr>
                </thead>
                <tbody>
                  {analysis.namespaces.map((n) => (
                    <tr key={n.namespace}>
                      <td>
                        <code>{n.namespace}</code>
                      </td>
                      <td>{n.items}</td>
                      <td>{n.recipes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {analysis.guide.categories.length > 0 && (
            <section className="card">
              <h3>Guidebook found</h3>
              <p className="preview-line">
                Under namespace <code>{analysis.guide.namespace}</code>: homepage categories and the entries beneath
                each. These become editable categories/entries when you import.
              </p>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Entries</th>
                  </tr>
                </thead>
                <tbody>
                  {analysis.guide.categories.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <code>{c.id}</code>
                      </td>
                      <td>{c.entryCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          <section className="card">
            <h3>Conflicts &amp; checks</h3>
            <ConflictList conflicts={analysis.conflicts} />
          </section>

          {analysis.items.length > 0 && (
            <section className="card">
              <h3>Item identifiers ({analysis.items.length})</h3>
              <ul className="id-list">
                {analysis.items.map((i) => (
                  <li key={i.path}>
                    <code>{i.identifier}</code> <span className="muted">{i.path}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="stat">
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}
